import { useState, useEffect } from "react";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import moment from "moment";
import Swal from "sweetalert2";
import { message } from "antd";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import { addlog } from "../../utils/ApiCalls/Logs";
import {
  updateCurrentActiveSailing,
  deleteSailingPageData,
} from "../../utils/ApiCalls/SailingData";
import { SAILING_PAGE_LOG_TYPES } from "../../constants/logging";
import { formatDateView } from "../../utils/CommonFunctions/utils";
import { deleteDailyPlanner } from "../../utils/ApiCalls/DailyPlanner/index";
import ConfirmModal from "../ConfirmModal/ConfirmModal";

// 🔴 Custom red switch
const RedSwitch = styled(Switch)(({ theme }) => ({
  transform: "scale(1.2)",
  marginLeft: "-6px",
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#389e0d",
    "&:hover": {
      backgroundColor: "rgba(24, 215, 42, 0.1)",
    },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#389e0d",
  },
  "& .MuiSwitch-track": {
    backgroundColor: "#ccc",
  },
  // 🧩 Prevent dull look when non-editable
  "&.Mui-disabled": {
    opacity: 1,
    pointerEvents: "none",
  },
  "&.Mui-disabled .MuiSwitch-track": {
    backgroundColor: "#d7182a !important",
  },
  "&.Mui-disabled .MuiSwitch-thumb": {
    color: "#d7182a !important",
  },
}));

export default function SailingToggle({
  data,
  activeSailing,
  setActiveSailing,
  isEditable = false,
  onStatusChange,
  enableDelete = false,
  getData,
  page_count = 0,
}) {
  const [checked, setChecked] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [{ userDetails }] = useDataLayerValue();

  useEffect(() => {
    setChecked(data.package_id === activeSailing?.packageId);
  }, [data.package_id, activeSailing]);

  const deleteSailing = async (vesselId, id) => {
    try {
      const res = await deleteSailingPageData(vesselId, id);

      if (res?.error) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: res.error,
        });
        return;
      }

      message.success("Sailing deleted successfully");
      getData();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: err?.message || "Something went wrong",
      });
    }
  };

  const deletePlanner = async (vesselId, id) => {
    try {
      const res = await deleteDailyPlanner(vesselId, id);

      if (res?.error) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: res.error,
        });
        return;
      }

      message.success("Daily Planner deleted successfully");
      getData();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: err?.message || "Something went wrong",
      });
    }
  };

  const handleToggle = async (event) => {
    if (!isEditable) return;

    const newChecked = event.target.checked;
    if (checked && !newChecked) return; // prevent turning off

    if (!checked && newChecked) {
      // Proceed with toggle directly
      await proceedWithToggle();
    }
  };

  const proceedWithToggle = async () => {
    setChecked(true);
    const formatDate = (dateStr) => dateStr?.split(" ")[0] || "";

    const payload = {
      vesselId: +data.vessel_id,
      packageId: data.package_id,
      duration: +data.duration,
      sailingDate: formatDate(data.sailing_date),
      endDate: formatDate(data.end_date),
    };

    try {
      const response = await updateCurrentActiveSailing(
        activeSailing.id,
        payload,
      );
      setActiveSailing(response);
      
      await addlog(
        SAILING_PAGE_LOG_TYPES.ACTIONS.STATUS_ACTIVATED,
        SAILING_PAGE_LOG_TYPES.MODULE,
        {
          title: `Sailing activated: ${moment(data.sailing_date).format(formatDateView("/"))} to ${moment(data.end_date).format(formatDateView("/"))} | ${data.duration} Nights`,
          vessel_id: data.vessel_id,
          package_id: data.package_id,
          sailing_date: data.sailing_date,
          end_date: data.end_date,
          duration: data.duration,
          vessel_name: data.vessel_name || "Unknown Vessel",
          previous_active_sailing: activeSailing?.packageId,
        },
        userDetails.id,
      );
    } catch (err) {
      console.error("❌ Failed to update sailing:", err);
      setChecked(false); // Reset toggle on error
    }
  };

  const tooltipMessage = !isEditable
    ? `To ${
        !checked ? "enable" : "disable"
      } this planner, please enable its respective cruise from "Cruise Page" section.`
    : "";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {/* Tooltip only for Switch */}
      <Tooltip
        title={tooltipMessage}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              fontSize: "0.95rem",
              padding: "8px 12px",
              borderRadius: "8px",
            },
          },
        }}
      >
        <span>
          <RedSwitch
            checked={checked}
            onChange={handleToggle}
            readOnly={!isEditable}
          />
        </span>
      </Tooltip>

      {enableDelete && (
        <>
          <ConfirmModal
            open={showDeleteModal}
            setOpen={setShowDeleteModal}
            title={isEditable ? "Delete Sailing" : "Delete Daily Planner"}
            message={page_count > 0 
              ? `: This will permanently delete the sailing and its ${page_count} page(s). Are you sure?` 
              : "Are you sure you want to delete this?"}
            confirmText="Yes, Delete"
            cancelText="Cancel"
            onConfirm={() =>
              isEditable
                ? deleteSailing(data.vessel_id, data.id)
                : deletePlanner(data.vessel_id, data.id)
            }
            width="450px"
            confirmColor="#d7182a"
          />
          <div>
            <button
              style={{
                backgroundColor: "#d7182a",
                color: "#fff",
                border: "none",
                width: "30px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = "#b31322")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "#d7182a")
              }
              onClick={() => setShowDeleteModal(true)}
            >
              <i
                className="ri-delete-bin-line"
                title="Delete"
                style={{ fontSize: "20px" }}
              ></i>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
